import Phaser from 'phaser';
import { Rift, RiftScar } from '../../types/rifts';

interface RiftNode {
  rift: Rift;
  x: number;
  y: number;
  connections: string[];
}

export class RiftNavigatorScene extends Phaser.Scene {
  private rifts: Rift[] = [];
  private scars: RiftScar[] = [];
  private currentRiftId: string = 'core';
  private selectedRiftId: string | null = null;
  private nodes: Map<string, RiftNode> = new Map();
  private nodeGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private connectionGraphics: Phaser.GameObjects.Graphics | null = null;
  private isLoaded: boolean = false;

  constructor() {
    super({ key: 'RiftNavigatorScene' });
  }

  async create(): Promise<void> {
    console.log('🗺️ [RIFT NAVIGATOR] Scene created');
    
    // Dark background
    this.cameras.main.setBackgroundColor('#0a0e1a');
    
    // Load data
    await this.loadData();
    
    if (this.isLoaded) {
      this.calculateNodePositions();
      // Defer drawing until next frame to ensure camera is fully initialized
      this.time.delayedCall(0, () => {
        this.drawNavigator();
        this.setupInput();
      });
    }

    // Listen for rift traversal events to refresh data
    this.events.on('rift-traversal-success', async () => {
      console.log('[RIFT NAVIGATOR] Rift traversal detected, refreshing data...');
      await this.loadData();
      if (this.isLoaded) {
        this.calculateNodePositions();
        this.drawNavigator();
      }
    });
  }

  private async loadData(): Promise<void> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const playerId = this.registry.get('playerId') || 'unknown';
      
      console.log('[RIFT NAVIGATOR] Fetching from:', `${apiUrl}/api/starmap/${playerId}`);
      
      const response = await fetch(`${apiUrl}/api/starmap/${playerId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      this.rifts = data.rifts || [];
      this.scars = data.scars || [];
      this.currentRiftId = data.currentRiftId || 'core';
      this.selectedRiftId = this.currentRiftId;
      
      console.log('[RIFT NAVIGATOR] Loaded', this.rifts.length, 'rifts and', this.scars.length, 'scars');
      
      this.isLoaded = true;
      this.events.emit('navigatorLoaded');
    } catch (error) {
      console.error('[RIFT NAVIGATOR] Error loading data:', error);
    }
  }

  private calculateNodePositions(): void {
    // Position core at center
    const coreRift = this.rifts.find(r => r.id === 'core');
    if (coreRift) {
      const connections: string[] = [];
      this.scars.forEach(scar => {
        if (scar.sourceRift === coreRift.id) {
          connections.push(scar.destinationRift);
        }
      });
      
      this.nodes.set(coreRift.id, {
        rift: coreRift,
        x: 0,
        y: 0,
        connections,
      });
    }
    
    // Position Layer 1 rifts in cardinal directions based on their ID
    const layer1Rifts = this.rifts.filter(r => r.layer === 1);
    const distance = 300;
    
    layer1Rifts.forEach(rift => {
      let x = 0;
      let y = 0;
      
      // Position based on cardinal direction in ID
      if (rift.id.includes('_NE')) {
        x = distance;
        y = -distance;
      } else if (rift.id.includes('_NW')) {
        x = -distance;
        y = -distance;
      } else if (rift.id.includes('_SE')) {
        x = distance;
        y = distance;
      } else if (rift.id.includes('_SW')) {
        x = -distance;
        y = distance;
      }
      
      const connections: string[] = [];
      this.scars.forEach(scar => {
        if (scar.sourceRift === rift.id) {
          connections.push(scar.destinationRift);
        }
      });
      
      this.nodes.set(rift.id, {
        rift,
        x,
        y,
        connections,
      });
    });
  }

  private drawNavigator(): void {
    // Get dimensions from camera, scale manager, or game config, with fallback to window size
    const width = (this.cameras.main.width || this.scale.width || this.game.config.width || window.innerWidth) as number;
    const height = (this.cameras.main.height || this.scale.height || this.game.config.height || window.innerHeight) as number;
    
    if (!width || !height) {
      console.error('[RIFT NAVIGATOR] Camera dimensions unavailable:', { width, height });
      return;
    }
    
    // Draw grid
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x1a1a2e, 1);
    const gridSize = 100;
    for (let x = 0; x < width; x += gridSize) {
      gridGraphics.lineBetween(x, 0, x, height);
    }
    for (let y = 0; y < height; y += gridSize) {
      gridGraphics.lineBetween(0, y, width, y);
    }
    
    // Draw connections
    this.connectionGraphics = this.add.graphics();
    this.scars.forEach(scar => {
      const sourceNode = this.nodes.get(scar.sourceRift);
      const destNode = this.nodes.get(scar.destinationRift);
      
      if (sourceNode && destNode && this.connectionGraphics) {
        const start = this.worldToScreen(sourceNode.x, sourceNode.y);
        const end = this.worldToScreen(destNode.x, destNode.y);
        
        const isDiscovered = scar.isDiscovered;
        const alpha = isDiscovered ? 0.6 : 0.2;
        const color = scar.stability === 'sealed' ? 0xff4444 : 0xfbbf24;
        
        this.connectionGraphics.lineStyle(isDiscovered ? 2 : 1, color, alpha);
        
        // Draw dashed line for sealed scars
        if (scar.stability === 'sealed') {
          // Draw dashed line manually
          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const dashLength = 5;
          const gapLength = 5;
          const totalLength = dashLength + gapLength;
          const numDashes = Math.floor(distance / totalLength);
          
          for (let i = 0; i < numDashes; i++) {
            const t1 = (i * totalLength) / distance;
            const t2 = ((i * totalLength) + dashLength) / distance;
            const x1 = start.x + dx * t1;
            const y1 = start.y + dy * t1;
            const x2 = start.x + dx * t2;
            const y2 = start.y + dy * t2;
            this.connectionGraphics.lineBetween(x1, y1, x2, y2);
          }
        } else {
          this.connectionGraphics.lineBetween(start.x, start.y, end.x, end.y);
        }
      }
    });
    
    // Draw nodes
    this.nodes.forEach((node, riftId) => {
      const pos = this.worldToScreen(node.x, node.y);
      const isCurrent = riftId === this.currentRiftId;
      const isSelected = riftId === this.selectedRiftId;
      const isDiscovered = node.rift.discoveredAt !== null || node.rift.isPublic;
      
      const size = 40;
      const alpha = isDiscovered ? 1.0 : 0.3;
      const color = this.getAnomalyColor(node.rift.anomalyLevel);
      
      const graphics = this.add.graphics();
      graphics.fillStyle(color, alpha);
      graphics.fillRect(pos.x - size / 2, pos.y - size / 2, size, size);
      
      if (isCurrent) {
        // Current location is indicated by the node itself being filled
        // No additional visual indicator needed
      } else if (isSelected) {
        graphics.lineStyle(2, 0xffffff, 1);
        graphics.strokeRect(pos.x - size / 2 - 2, pos.y - size / 2 - 2, size + 4, size + 4);
      }
      
      graphics.lineStyle(1, color, alpha * 0.7);
      graphics.strokeRect(pos.x - size / 2, pos.y - size / 2, size, size);
      
      // Always show anomaly level inside the node
      const levelText = this.add.text(pos.x, pos.y, node.rift.anomalyLevel.toFixed(1), {
        fontSize: '12px',
        fontFamily: 'Sometype Mono, monospace',
        color: '#000000',
        fontStyle: 'bold',
      });
      levelText.setOrigin(0.5);
      
      // Show name below node if discovered
      if (isDiscovered) {
        const nameText = this.add.text(pos.x, pos.y + size / 2 + 15, node.rift.name, {
          fontSize: '11px',
          fontFamily: 'Sometype Mono, monospace',
          color: '#ffffff',
        });
        nameText.setOrigin(0.5);
      }
      
      // Make interactive
      const hitArea = new Phaser.Geom.Rectangle(pos.x - size / 2 - 5, pos.y - size / 2 - 5, size + 10, size + 10);
      graphics.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
      
      graphics.on('pointerdown', () => {
        this.selectedRiftId = riftId;
        this.events.emit('riftSelected', node.rift);
        this.redrawNodes();
      });
      
      this.nodeGraphics.set(riftId, graphics);
    });
    
    // Center camera on current rift
    const currentNode = this.nodes.get(this.currentRiftId);
    if (currentNode) {
      const screenPos = this.worldToScreen(currentNode.x, currentNode.y);
      this.cameras.main.centerOn(screenPos.x, screenPos.y);
    }
  }

  private redrawNodes(): void {
    this.nodeGraphics.forEach((graphics, riftId) => {
      const node = this.nodes.get(riftId);
      if (!node) return;
      
      const pos = this.worldToScreen(node.x, node.y);
      const isCurrent = riftId === this.currentRiftId;
      const isSelected = riftId === this.selectedRiftId;
      const isDiscovered = node.rift.discoveredAt !== null || node.rift.isPublic;
      
      const size = 40;
      const alpha = isDiscovered ? 1.0 : 0.3;
      const color = this.getAnomalyColor(node.rift.anomalyLevel);
      
      graphics.clear();
      graphics.fillStyle(color, alpha);
      graphics.fillRect(pos.x - size / 2, pos.y - size / 2, size, size);
      
      if (isCurrent) {
        graphics.lineStyle(3, 0xfbbf24, 1);
        graphics.strokeRect(pos.x - size / 2 - 4, pos.y - size / 2 - 4, size + 8, size + 8);
      } else if (isSelected) {
        graphics.lineStyle(2, 0xffffff, 1);
        graphics.strokeRect(pos.x - size / 2 - 2, pos.y - size / 2 - 2, size + 4, size + 4);
      }
      
      graphics.lineStyle(1, color, alpha * 0.7);
      graphics.strokeRect(pos.x - size / 2, pos.y - size / 2, size, size);
    });
  }

  private setupInput(): void {
    // Zoom with mouse wheel
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: any[], _deltaX: number, deltaY: number) => {
      const zoomAmount = deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Phaser.Math.Clamp(this.cameras.main.zoom + zoomAmount, 0.3, 2);
      this.cameras.main.setZoom(newZoom);
    });
    
    // Pan with right mouse drag
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        isDragging = true;
        dragStartX = pointer.x;
        dragStartY = pointer.y;
      }
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (isDragging) {
        const deltaX = pointer.x - dragStartX;
        const deltaY = pointer.y - dragStartY;
        this.cameras.main.scrollX -= deltaX;
        this.cameras.main.scrollY -= deltaY;
        dragStartX = pointer.x;
        dragStartY = pointer.y;
      }
    });
    
    this.input.on('pointerup', () => {
      isDragging = false;
    });
  }

  private worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    return {
      x: worldX + width / 2,
      y: worldY + height / 2,
    };
  }

  private getAnomalyColor(anomalyLevel: number): number {
    if (anomalyLevel === 0) return 0x4488ff;
    if (anomalyLevel <= 2) return 0x88ff44;
    if (anomalyLevel <= 4) return 0xffff44;
    if (anomalyLevel <= 6) return 0xffaa44;
    if (anomalyLevel <= 8) return 0xff6644;
    return 0xff4444;
  }

  getCurrentRiftId(): string {
    return this.currentRiftId;
  }

  getSelectedRift(): Rift | null {
    if (!this.selectedRiftId) return null;
    const node = this.nodes.get(this.selectedRiftId);
    return node ? node.rift : null;
  }

  getConnectedScars(): RiftScar[] {
    if (!this.selectedRiftId) return [];
    return this.scars.filter(s => s.sourceRift === this.selectedRiftId);
  }

  getRifts(): Rift[] {
    return this.rifts;
  }

  getScars(): RiftScar[] {
    return this.scars;
  }
}
